import { useCallback, useMemo } from 'react';
import { useAppSelector } from './useAppSelector.hook';
import { getKCUser } from '../redux/App/App.slice';
import { EmailTemplateVariables } from '../types/emailTemplate.types';
import { useTranslation } from 'react-i18next';
import { getCandidateSelected } from '../redux/Candidate/Candidate.slice';

interface Props {
  templateText: string;
}

interface TemplateVariable {
  name: string;
  value: string;

  // Значение, которым пытаемся заменить переменную
  replaceToValue: string;

  // Значение, которое вставится как максимальное возможное значение для
  // этой переменной, если replaceToValue отсутствует
  maxReplaceToValue: string;

  // Не предполагается отрисовка на странице
  hidden?: boolean;
}

const OPEN_VARIABLE_REGEX = /{%\s/g;
const CLOSE_VARIABLE_REGEX = /\s%}/g;
// Это лимит URL, но в нашем случае лимит mailto ссылки
const MAX_MAILTO_LENGTH = 6000;

const useEmailTemplate = ({ templateText }: Props) => {
  const user = useAppSelector(getKCUser);
  const { item: candidate } = useAppSelector(getCandidateSelected);
  const { t } = useTranslation();

  const templateVariables = useMemo(() => {
    return [
      {
        name: t('candidateName'),
        value: EmailTemplateVariables.CANDIDATE_FULL_NAME,
        replaceToValue: candidate?.fullName || '',
        // У нас установлен лимит по 50 на имя и фамилию, но одна русская буква
        // кодируется через encodeURI() в 6 символов ((50 + 50) * 6) = 600
        // ВРЕМЕННО ПОНИЖЕН ЛИМИТ ДО 40 ((20+20) * 6) = 108, ЧТОБЫ ПОМЕЩАЛИСЬ ДЕФОЛТНЫЕ ШАБЛОНЫ
        // maxReplaceToValue: new Array(600 + 1).join('0'),
        maxReplaceToValue: new Array(240 + 1).join('0'),
      },
      {
        name: t('senderName'),
        value: EmailTemplateVariables.ME_FULL_NAME,
        replaceToValue: user.fullName || '',
        // У нас установлен лимит по 50 на имя и фамилию, но одна русская буква
        // кодируется через encodeURI() в 6 символов ((50 + 50) * 6) = 600
        // ВРЕМЕННО ПОНИЖЕН ЛИМИТ ДО 40 ((20+20) * 6) = 108, ЧТОБЫ ПОМЕЩАЛИСЬ ДЕФОЛТНЫЕ ШАБЛОНЫ
        // maxReplaceToValue: new Array(600 + 1).join('0'),
        maxReplaceToValue: new Array(240 + 1).join('0'),
      },
      {
        name: '',
        value: EmailTemplateVariables.CANDIDATE_EMAIL,
        replaceToValue: candidate?.email || '',
        // Стоит лимит в 250 символов на email
        // ВРЕМЕННО ПОНИЖЕН ЛИМИТ ДО 50, ЧТОБЫ ПОМЕЩАЛИСЬ ДЕФОЛТНЫЕ ШАБЛОНЫ
        // maxReplaceToValue: new Array(250 + 1).join('0'),
        maxReplaceToValue: new Array(50 + 1).join('0'),
        hidden: true,
      },
    ] as TemplateVariable[];
  }, [candidate?.email, candidate?.fullName, t, user.fullName]);

  const replaceVariables = useCallback(
    (
      text: string,
      // Если значения нет, то заполним максимально возможным значением
      fillWithMaxValue?: boolean,
    ) => {
      return templateVariables.reduce((accumulator, variable) => {
        return accumulator.replaceAll(
          variable.value,
          fillWithMaxValue
            ? variable.replaceToValue || variable.maxReplaceToValue
            : variable.replaceToValue,
        );
      }, text);
    },
    [templateVariables],
  );

  const openMailClient = () => {
    window.location.href = `mailto:${candidate?.email}?body=${encodeURIComponent(replaceVariables(templateText))}`;
  };

  const isTemplateVariablesCorrect = useMemo(() => {
    const openMatches = templateText.match(OPEN_VARIABLE_REGEX) || [];
    const closeMatches = templateText.match(CLOSE_VARIABLE_REGEX) || [];

    return openMatches.length === closeMatches.length;
  }, [templateText]);

  const isTemplateLengthCorrect = useMemo(() => {
    const mailtoLength = `mailto:&body=`.length;
    const emailLength = replaceVariables(EmailTemplateVariables.CANDIDATE_EMAIL, true).length;
    const bodyLength = encodeURIComponent(replaceVariables(templateText, true)).length;

    return mailtoLength + emailLength + bodyLength <= MAX_MAILTO_LENGTH;
  }, [replaceVariables, templateText]);

  return {
    templateVariables,
    replaceVariables,
    openMailClient,
    isTemplateVariablesCorrect,
    isTemplateLengthCorrect,
  };
};

export default useEmailTemplate;
